import { memo, useMemo, useState } from 'react'
import { Grid } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import Section from 'components/common/Section/Section'
import FormField from 'components/inputs/FormField/FormField'
import ProductInfoItem from '../ProductInfoItem/ProductInfoItem'
import { EFormFieldType } from 'enum/EFormFieldType'
import { FormFieldType, LabelValueType } from 'types/types'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { useUpdateProductAudience } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'

interface AudienceFormValues {
  audience: string;
  targetGender: string;
}

// Empty rather than absent, because a select has to be able to say "nobody in
// particular": a product that addresses every age is not a product nobody has
// answered for, and the two look the same if the only way to clear the field is
// to leave it alone.
const NOT_SET = '';

const AGE_GROUPS: LabelValueType[] = [
  { key: 'not-set', value: NOT_SET, label: 'Not set' },
  { key: 'baby', value: 'baby', label: 'Baby' },
  { key: 'kids', value: 'kids', label: 'Kids' },
  { key: 'adult', value: 'adult', label: 'Adult' },
];

const GENDERS: LabelValueType[] = [
  { key: 'not-set', value: NOT_SET, label: 'Not set' },
  { key: 'women', value: 'women', label: 'Women' },
  { key: 'men', value: 'men', label: 'Men' },
];

const labelFor = (options: LabelValueType[], value: string | null) =>
  options.find((option) => option.value === value)?.label ?? null;

// Who the pack is for. Its own section rather than two more fields in Product
// Information: nothing on this page reads a label to fill these in - they are
// answered on the raw product and carried over on promotion - so an Analyze
// button beside them would be one that never touches them.
function AudienceDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();

  const methods = useForm<AudienceFormValues>({
    defaultValues: {
      audience: product.audience ?? NOT_SET,
      targetGender: product.targetGender ?? NOT_SET,
    },
  });

  const fields: FormFieldType[] = useMemo(() => {
    return [
      {
        name: 'audience',
        type: EFormFieldType.DROPDOWN,
        placeholder: 'Age group',
        label: 'Age Group',
        required: false,
        options: AGE_GROUPS,
        visible: true,
      },
      {
        name: 'targetGender',
        type: EFormFieldType.DROPDOWN,
        placeholder: 'Gender',
        label: 'Gender',
        required: false,
        options: GENDERS,
        visible: true,
      },
    ];
  }, []);

  const { mutate: updateMutation, isLoading } = useUpdateProductAudience(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product audience has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => { },
  )

  const handleSave = () => {
    const values = methods.getValues();
    updateMutation({
      id: product.id,
      // Empty is the reviewer saying "nobody in particular", which the column
      // holds as null rather than as an empty string.
      audience: values.audience || null,
      targetGender: values.targetGender || null,
    });
  }

  const handleCancel = () => {
    methods.reset({
      audience: product.audience ?? NOT_SET,
      targetGender: product.targetGender ?? NOT_SET,
    });
    setIsEditing(false);
  }

  return (
    <FormProvider {...methods}>
      <Section
        title={'Audience'}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => setIsEditing((prev) => !prev),
                disabled: isLoading,
              }
            ]),
        ]}
        content={
          isEditing ? (
            // Section lays its content out in a grid already, and FormField
            // returns the grid item, so the fields go in as they are.
            <>
              {fields.map((field) => (
                <FormField key={field.name} field={field} />
              ))}
            </>
          ) : (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <ProductInfoItem
                  label={'Age Group'}
                  value={labelFor(AGE_GROUPS, product.audience ?? null)}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <ProductInfoItem
                  label={'Gender'}
                  value={labelFor(GENDERS, product.targetGender ?? null)}
                />
              </Grid>
            </>
          )
        }
      />
    </FormProvider>
  )
}

export default memo(AudienceDetails)
